import { useState, useCallback } from 'react'
import type {
  Ticket,
  RefundOption,
  RefundRequest,
  RefundFlowState,
} from '../types'
import { MOCK_TICKETS } from '../constants/data'

const REFUND_STEPS = [
  { id: 'ticket', label: 'Ticket' },
  { id: 'eligibility', label: 'Eligibility' },
  { id: 'options', label: 'Options' },
  { id: 'kyc', label: 'Verification' },
  { id: 'processing', label: 'Processing' },
  { id: 'confirmation', label: 'Done' },
]

// Fee constants
const INTERNAL_FEE_PERCENTAGE = 0.1 // 10% for internal bookings
const EXTERNAL_FEE_PERCENTAGE = 0.15 // 15% for external bookings
const STORE_CREDIT_BONUS_PERCENTAGE = 0.1 // 10% bonus

// Helper to check if ticket is internal (bought from AeroZeep)
// For demo: tickets starting with 'AZ' or specific PNRs are internal
const isInternalTicket = (ticket: Ticket | null): boolean => {
  if (!ticket) return false
  // Mock logic: tickets with PNR starting with 'AZ' or specific airlines are internal
  return ticket.pnr.startsWith('AZ') || ticket.flight.airline.id === 'air-peace'
}

const initialState: RefundFlowState = {
  step: 0,
  ticketNumber: '',
  ticket: null,
  refundOption: null,
  email: '',
  phone: '',
  verificationCode: '',
  generatedCode: null,
  isCodeSent: false,
  bvn: '',
  bankCode: '',
  accountNumber: '',
  accountName: '',
  isValidating: false,
  isProcessing: false,
  error: null,
  refundRequest: null,
}

export function useRefundFlow() {
  const [state, setState] = useState<RefundFlowState>(initialState)

  const updateState = useCallback((updates: Partial<RefundFlowState>) => {
    setState((prev) => ({ ...prev, ...updates }))
  }, [])

  const setTicketNumber = useCallback(
    (ticketNumber: string) => {
      updateState({ ticketNumber, error: null })
    },
    [updateState],
  )

  const validateTicket = useCallback(async () => {
    updateState({ isValidating: true, error: null })

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000))

    // Mock validation - in real app, this would call GDS/NCAA Avitech
    const normalizedInput = state.ticketNumber.toUpperCase().trim()

    // Check if ticket exists in MOCK_TICKETS
    const foundTicket = MOCK_TICKETS[normalizedInput]

    if (foundTicket) {
      updateState({
        isValidating: false,
        ticket: foundTicket,
        step: 1,
      })
      return true
    } else if (normalizedInput === 'REFUND') {
      updateState({
        isValidating: false,
        error: 'This ticket has already been refunded.',
      })
      return false
    } else {
      updateState({
        isValidating: false,
        error: 'Ticket not found. Please check the number and try again.',
      })
      return false
    }
  }, [state.ticketNumber, updateState])

  const setRefundOption = useCallback(
    (option: RefundOption) => {
      updateState({ refundOption: option, error: null })
    },
    [updateState],
  )

  const setEmail = useCallback(
    (email: string) => {
      updateState({ email, error: null })
    },
    [updateState],
  )

  const setPhone = useCallback(
    (phone: string) => {
      updateState({ phone, error: null })
    },
    [updateState],
  )

  const setVerificationCode = useCallback(
    (verificationCode: string) => {
      updateState({ verificationCode, error: null })
    },
    [updateState],
  )

  const sendVerificationCode = useCallback(async () => {
    updateState({ isValidating: true, error: null })

    // For demo purposes, we'll skip strict validation against the ticket
    // In a real app, we would validate:
    // const ticketEmail = state.ticket?.passenger.email.toLowerCase().trim()
    // const ticketPhone = state.ticket?.passenger.phone.replace(/\s+/g, '')
    // const inputEmail = state.email.toLowerCase().trim()
    // const inputPhone = state.phone.replace(/\s+/g, '')

    // if (ticketEmail !== inputEmail) { ... }
    // if (ticketPhone !== inputPhone) { ... }

    // Simulate sending verification code via SMS/Email
    await new Promise((resolve) => setTimeout(resolve, 1500))

    // Generate a random 6-digit code for demo purposes
    const generatedCode = Math.floor(100000 + Math.random() * 900000).toString()
    console.log('Verification code generated:', generatedCode)

    updateState({
      isValidating: false,
      isCodeSent: true,
      generatedCode, // Store the generated code
    })
    return generatedCode // Return the code so UI can display it
  }, [state.email, state.phone, state.ticket, updateState])

  const verifyCode = useCallback(async () => {
    updateState({ isValidating: true, error: null })

    // Simulate API verification
    await new Promise((resolve) => setTimeout(resolve, 1000))

    // Verify against the generated code
    if (
      state.verificationCode === state.generatedCode ||
      state.verificationCode === '123456'
    ) {
      updateState({
        isValidating: false,
        error: null,
      })
      return true
    } else {
      updateState({
        isValidating: false,
        error: 'Invalid verification code. Please check and try again.',
      })
      return false
    }
  }, [state.verificationCode, state.generatedCode, updateState])

  const setBvn = useCallback(
    (bvn: string) => {
      updateState({ bvn, error: null })
    },
    [updateState],
  )

  const setBankCode = useCallback(
    (bankCode: string) => {
      updateState({ bankCode, error: null })
    },
    [updateState],
  )

  const setAccountNumber = useCallback(
    (accountNumber: string) => {
      updateState({ accountNumber, error: null })
    },
    [updateState],
  )

  const verifyKyc = useCallback(async () => {
    updateState({ isValidating: true, error: null })

    // First verify the code
    const codeValid = await verifyCode()
    if (!codeValid) {
      return false
    }

    // Simulate NIBSS BVN verification
    await new Promise((resolve) => setTimeout(resolve, 2500))

    // Mock verification - in real app, this would call NIBSS
    if (state.bvn.length !== 11) {
      updateState({
        isValidating: false,
        error: 'Invalid BVN. Please enter a valid 11-digit BVN.',
      })
      return false
    }

    // For bank payout, also validate bank details
    if (state.refundOption === 'bank_payout') {
      if (!state.bankCode) {
        updateState({
          isValidating: false,
          error: 'Please select your bank.',
        })
        return false
      }

      if (state.accountNumber.length !== 10) {
        updateState({
          isValidating: false,
          error:
            'Invalid account number. Please enter a valid 10-digit account number.',
        })
        return false
      }
    }

    // All validations passed
    updateState({
      isValidating: false,
      accountName: `${state.ticket?.passenger.firstName} ${state.ticket?.passenger.lastName}`,
      step: 4,
    })
    return true
  }, [
    state.bvn,
    state.accountNumber,
    state.bankCode,
    state.refundOption,
    state.ticket,
    updateState,
    verifyCode,
  ])

  // Calculate refund amounts dynamically
  const calculateRefund = useCallback(() => {
    if (!state.ticket) return null

    const isInternal = isInternalTicket(state.ticket)
    const feePercentage = isInternal
      ? INTERNAL_FEE_PERCENTAGE
      : EXTERNAL_FEE_PERCENTAGE
    const processingFee = Math.round(
      state.ticket.originalAmount * feePercentage,
    )

    const netRefund = state.ticket.originalAmount - processingFee
    const bonusAmount = Math.round(netRefund * STORE_CREDIT_BONUS_PERCENTAGE)

    return {
      originalAmount: state.ticket.originalAmount,
      processingFee,
      netRefund,
      storeCreditAmount: netRefund + bonusAmount,
      bonusAmount,
      feePercentage: feePercentage * 100, // Return percentage for display
      isInternal,
    }
  }, [state.ticket])

  const processRefund = useCallback(
    async (onProgress: (percentage: number, status: string) => void) => {
      updateState({ isProcessing: true, error: null })

      const statuses = [
        { percentage: 10, status: 'Validating ticket with airline...' },
        { percentage: 30, status: 'Confirming refund eligibility...' },
        { percentage: 50, status: 'Processing refund request...' },
        { percentage: 70, status: 'Transferring funds...' },
        { percentage: 90, status: 'Finalizing transaction...' },
        { percentage: 100, status: 'Complete!' },
      ]

      for (const { percentage, status } of statuses) {
        await new Promise((resolve) => setTimeout(resolve, 1500))
        onProgress(percentage, status)
      }

      // Create refund request with dynamic fee
      const ticket = state.ticket!
      const refundDetails = calculateRefund()!

      const refundRequest: RefundRequest = {
        id: `REF-${Date.now()}`,
        ticket,
        status: 'completed',
        refundOption: state.refundOption!,
        originalAmount: ticket.originalAmount,
        processingFee: refundDetails.processingFee,
        netRefund: refundDetails.netRefund,
        bonusAmount:
          state.refundOption === 'store_credit' ? refundDetails.bonusAmount : 0,
        bankDetails:
          state.refundOption === 'bank_payout'
            ? {
                bankName: 'Selected Bank',
                bankCode: state.bankCode,
                accountNumber: state.accountNumber,
                accountName: state.accountName,
              }
            : undefined,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        completedAt: new Date().toISOString(),
      }

      updateState({
        isProcessing: false,
        refundRequest,
        step: 5,
      })

      return refundRequest
    },
    [
      state.ticket,
      state.refundOption,
      state.bankCode,
      state.accountNumber,
      state.accountName,
      updateState,
      calculateRefund,
    ],
  )

  const nextStep = useCallback(() => {
    if (state.step < REFUND_STEPS.length - 1) {
      updateState({ step: state.step + 1, error: null })
    }
  }, [state.step, updateState])

  const prevStep = useCallback(() => {
    if (state.step > 0) {
      updateState({ step: state.step - 1, error: null })
    }
  }, [state.step, updateState])

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 0 && step < REFUND_STEPS.length) {
        updateState({ step, error: null })
      }
    },
    [updateState],
  )

  const reset = useCallback(() => {
    setState(initialState)
  }, [])

  const resendVerificationCode = useCallback(async () => {
    updateState({ isValidating: true, error: null })

    // Simulate resending code
    await new Promise((resolve) => setTimeout(resolve, 1500))

    const generatedCode = Math.floor(100000 + Math.random() * 900000).toString()
    console.log('Verification code resent:', generatedCode)

    updateState({
      isValidating: false,
      generatedCode,
    })
    return generatedCode
  }, [updateState])

  return {
    state,
    steps: REFUND_STEPS,
    setTicketNumber,
    validateTicket,
    setRefundOption,
    setEmail,
    setPhone,
    setVerificationCode,
    sendVerificationCode,
    resendVerificationCode,
    verifyCode,
    setBvn,
    setBankCode,
    setAccountNumber,
    verifyKyc,
    processRefund,
    nextStep,
    prevStep,
    goToStep,
    reset,
    calculateRefund,
  }
}
